import { SidebarRoutes } from '@components/routes/SidebarRoutes';
import { StudioType } from '@conversed/shared/src/central/types/studio.type';
import { LoadingSpinner } from '@conversed/ui/src/components/spinner/LoadingSpinner';
import { isRouteAllowedForStudioType } from '@helpers/routeHelpers';
import { useCompanySelector } from '@hooks/useCompanySelector';
import { useMainSelector } from '@hooks/useMainSelector';
import { usePermissions } from '@hooks/usePermissions';
import { usePlatformFeatures } from '@hooks/usePlatformFeatures';
import type React from 'react';
import { type ReactNode, Suspense, useMemo } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { EBreakpoint } from '../../constants/breakpoints';
import { MAIN_PAGE_ROUTE } from '../../constants/routes';
import { useMedia } from '../../hooks/useMedia';
import type { IRoute, TRouteType } from '../../interfaces/IRoutes';
import { NotFoundPage } from '../pages/NotFoundPage';
import { AuthenticatedAppRoutes } from '../routes/AuthenticatedAppRoutes';
import { RestrictedRoute } from '../routes/RestrictedRoute';
import { SettingsSidebarRouter } from '../settings-page/SettingsContent';

export const AuthenticatedRouter: React.FC = () => {
  const { down: isMobile } = useMedia(EBreakpoint.MD);
  const { hasPermissions } = usePermissions();
  const { selectedCompany } = useCompanySelector();
  const { hasPlatformFeature } = usePlatformFeatures();
  const {
    user: { currentUser },
  } = useMainSelector();

  const currentStudioType = selectedCompany?.studioType || StudioType.CHAT_AI;

  const filteredRoutes = useMemo(() => {
    const allRoutes = AuthenticatedAppRoutes(isMobile);

    return allRoutes.filter((route) => {
      // * Role check, not permission check: Settings sub-page permissions overlap with
      // * client-accessible features, so filtering by permission would not reliably
      // * block access. Gating on isSuperAdmin is the only safe boundary.
      if (route.superAdminOnly && !currentUser?.isSuperAdmin) {
        return false;
      }
      if (
        route.hiddenWhenFeatureActive &&
        hasPlatformFeature(route.hiddenWhenFeatureActive)
      ) {
        return false;
      }
      return isRouteAllowedForStudioType(route.studioTypes, currentStudioType);
    });
  }, [
    currentStudioType,
    isMobile,
    hasPlatformFeature,
    currentUser?.isSuperAdmin,
  ]);

  // Filter sidebar routes by studio type and permissions for default redirect
  const filteredSidebarRoutes = useMemo(() => {
    return SidebarRoutes.filter((route) => {
      const isStudioTypeAllowed = isRouteAllowedForStudioType(
        route.studioTypes,
        currentStudioType,
      );
      const hasRequiredPermissions = hasPermissions(route.permissions);

      return isStudioTypeAllowed && hasRequiredPermissions;
    });
  }, [currentStudioType, hasPermissions]);

  // Dynamically determine the default redirection path
  const defaultRedirectPath = useMemo(() => {
    const firstAvailablePage = filteredSidebarRoutes[0];
    return firstAvailablePage ? firstAvailablePage.to : '/not-found';
  }, [filteredSidebarRoutes]);

  return (
    <Suspense fallback={<LoadingSpinner visible />}>
      <Routes>
        <Route
          path={MAIN_PAGE_ROUTE}
          element={<Navigate to={defaultRedirectPath} />}
        />
        {filteredRoutes.map((route: IRoute) => {
          const { path, component, type, permissions, exact } = route;

          const routeTypeMap: Record<TRouteType, (route: IRoute) => ReactNode> =
            {
              restricted: ({ path, component, permissions, exact }) => (
                <Route
                  key={path}
                  path={exact ? path : `${path}/*`}
                  element={
                    <RestrictedRoute
                      requiredPermissions={permissions}
                      component={component}
                    />
                  }
                />
              ),
              default: ({ path, component: Component, exact }) => (
                <Route
                  key={path}
                  path={exact ? path : `${path}/*`}
                  element={<Component />}
                />
              ),
            };

          const routeType = type || 'default';
          return routeTypeMap[routeType]({
            path,
            component,
            type,
            permissions,
            exact,
          });
        })}

        {isMobile && <Route path='*' element={<SettingsSidebarRouter />} />}

        <Route path='*' element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
};
